import { Divider, Stack, Tooltip } from "@mantine/core";
import type { ReactElement } from "react";

import { useGetMe } from "../../api/generated/endpoints/identity/identity";
import type { TeamRole } from "../../api/generated/model";
import { managesBrands } from "../permissions";
import { BrandSelect } from "./BrandSelect";
import { NAV_BOTTOM, NAV_GROUPS, type NavItem } from "./nav";
import { targetRoot, useCurrentSection, useNavBrands } from "./nav-links";
import classes from "./shell.module.css";

interface SidebarProps {
  teamId: string;
  role: TeamRole;
  /** 240px with labels, or the 70px rail with tooltips. */
  open: boolean;
  /** Called when an item navigates: the phone drawer closes. */
  onNavigate?: () => void;
}

/** The brand select and the nav groups of spec 2.3. */
export function Sidebar({ teamId, role, open, onNavigate }: SidebarProps) {
  const isStaff = useGetMe({ query: { select: (me) => me.is_staff } }).data === true;
  const nav = useNavBrands(teamId);
  const active = useCurrentSection();
  const item = (each: NavItem) => (
    <SidebarItem
      key={each.label}
      item={each}
      open={open}
      active={each.label === active}
      href={targetRoot(each.target, teamId, nav.linkBrandId)}
      // A brand item with no brand to go to, once the list says there is none.
      noBrand={
        nav.brands?.length === 0 && each.target.scope === "brand"
          ? managesBrands(role)
            ? "Add a brand first"
            : "This team has no brand yet"
          : undefined
      }
      onNavigate={onNavigate}
    />
  );
  return (
    <>
      <BrandSelect teamId={teamId} role={role} open={open} onNavigate={onNavigate} />
      <Divider variant="dashed" className={classes.mainDivider} />
      <nav aria-label="Main" className={classes.nav}>
        {NAV_GROUPS.map((group, index) => (
          <div key={group.title} className={classes.group}>
            {open ? <p className={classes.groupTitle}>{group.title}</p> : null}
            {!open && index > 0 ? (
              <Divider variant="dashed" className={classes.groupDivider} />
            ) : null}
            <Stack gap={2}>{group.items.map(item)}</Stack>
          </div>
        ))}
        <div className={classes.bottom}>
          <Divider variant="dashed" className={classes.bottomDivider} />
          {NAV_BOTTOM.filter((each) => each.target.scope !== "staff" || isStaff).map(item)}
        </div>
      </nav>
    </>
  );
}

interface SidebarItemProps {
  item: NavItem;
  open: boolean;
  active: boolean;
  href: ReturnType<typeof targetRoot>;
  /** Why a brand item goes nowhere, when the team has no brand. */
  noBrand: string | undefined;
  onNavigate: (() => void) | undefined;
}

function SidebarItem({ item, open, active, href, noBrand, onNavigate }: SidebarItemProps) {
  const Icon = item.icon;
  const content = (
    <>
      <Icon size={20} strokeWidth={1.5} aria-hidden />
      {open ? <span className={classes.navLabel}>{item.label}</span> : null}
    </>
  );
  const shared = {
    className: classes.navItem,
    "data-compact": open ? undefined : true,
    "aria-label": open ? undefined : item.label,
  };
  let element: ReactElement;
  let note: string | undefined;
  if (href === undefined) {
    // Not `disabled`: a disabled button gets no hover, so its tooltip would never show.
    element = (
      <button type="button" aria-disabled="true" {...shared}>
        {content}
      </button>
    );
    note = item.target.scope === "soon" ? "Coming soon" : noBrand;
  } else {
    element = href({
      ...shared,
      "data-active": active ? true : undefined,
      "aria-current": active ? "page" : undefined,
      onClick: onNavigate,
      children: content,
    });
  }
  const label = open ? note : note === undefined ? item.label : `${item.label}: ${note}`;
  if (label === undefined) return element;
  return (
    <Tooltip label={label} position={open ? "top" : "right"}>
      {element}
    </Tooltip>
  );
}
